"use client";

/**
 * PlatformWorkspaceSwitcher — the small animated workspace-switcher
 * mock for /platform. Cycles through four states (chat → research →
 * code → news) every ~2.2s, with a tab-bar at the top, a mock content
 * pane that swaps with a blur-fade, and a footer hint at what each
 * workspace is doing right now.
 *
 * Pure presentation. Lives on the marketing site only — the real
 * surfaces are at chat.askfinz.ai, research.askfinz.ai, etc.
 */
import { useEffect, useRef, useState } from "react";
import {
  MessageSquare,
  BookOpenCheck,
  Code2,
  Newspaper,
} from "lucide-react";

type Workspace = {
  id: string;
  name: string;
  domain: string;
  icon: typeof MessageSquare;
  prompt: string;
  body: { tag: string; text: string }[];
  foot: string;
};

const WORKSPACES: Workspace[] = [
  {
    id: "chat",
    name: "Chat",
    domain: "chat.askfinz.ai",
    icon: MessageSquare,
    prompt: "Compare the two proposals — what did they actually agree on?",
    body: [
      { tag: "Agreement · 01", text: "Both honour the 2030 emission target without amendment." },
      { tag: "Agreement · 02", text: "Both keep household rebates tied to taxable income." },
      { tag: "Divergence · 01", text: "Proposal B accelerates the industrial-grid phase-out by 18 months." },
      { tag: "Divergence · 02", text: "Proposal A relaxes the coastal carve-out; B keeps it." },
    ],
    foot: "model · reasoning · 3.2s",
  },
  {
    id: "research",
    name: "Research",
    domain: "research.askfinz.ai",
    icon: BookOpenCheck,
    prompt: "Synthesise the literature on second-line antifungals for immunocompromised patients.",
    body: [
      { tag: "Source · NEJM 2024",   text: "Liposomal amphotericin remained first-line in 81% of cohorts…" },
      { tag: "Source · Lancet ID",   text: "Isavuconazole non-inferior, lower nephrotoxicity profile…" },
      { tag: "Source · Cochrane",    text: "Insufficient evidence for combination therapy in solid-organ recipients…" },
      { tag: "Source · askFinz/lib", text: "Your prior notebook from 18 Mar references the same review trail…" },
    ],
    foot: "11 citations · 4 branches · Researcher",
  },
  {
    id: "code",
    name: "Code",
    domain: "code.askfinz.ai",
    icon: Code2,
    prompt: "Backfill tests for the new session store; flag anything the old suite missed.",
    body: [
      { tag: "Plan · 04 steps",  text: "Scaffold spec file · cover happy path · cover replay · cover rotation." },
      { tag: "Drafted · 14 cases", text: "Including 3 the previous suite never exercised (token rotation under load)." },
      { tag: "Run · local",       text: "13 pass · 1 reveals a latent off-by-one in expiry boundary." },
      { tag: "Diff · ready",      text: "+312 / -187 across 7 files. Awaiting review before commit." },
    ],
    foot: "Coder · sandbox · 1m 04s",
  },
  {
    id: "news",
    name: "News",
    domain: "news.askfinz.ai",
    icon: Newspaper,
    prompt: "What broke overnight in semiconductors? Three-line summary, sources attached.",
    body: [
      { tag: "Cluster · 01", text: "TSMC raises 2026 capex on N3 demand — 8 outlets converging on the figure." },
      { tag: "Cluster · 02", text: "EU chip act phase 2 advances; UK signals divergent path." },
      { tag: "Cluster · 03", text: "ASML quarterly: bookings beat, China revenue down 14%." },
      { tag: "Audio brief",  text: "Six-minute briefing ready. Voice · Calm. Pace · 1.0×." },
    ],
    foot: "12 feeds · deduped to 27 stories · briefing ready",
  },
];

export function PlatformWorkspaceSwitcher() {
  const [idx, setIdx] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        const id = setInterval(() => {
          setIdx((i) => (i + 1) % WORKSPACES.length);
        }, 2400);
        // Stash on the element so cleanup can clear.
        (el as unknown as { __tid?: number }).__tid = id as unknown as number;
        obs.disconnect();
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      const tid = (el as unknown as { __tid?: number }).__tid;
      if (tid) clearInterval(tid);
    };
  }, []);

  const active = WORKSPACES[idx];
  const ActiveIcon = active.icon;

  return (
    <div ref={ref} className="relative w-full max-w-4xl mx-auto">
      <div
        aria-hidden
        className="absolute -inset-x-6 -inset-y-6 rounded-[32px] bg-foreground/[0.018] blur-2xl"
      />

      <div className="relative rounded-2xl border hairline-strong bg-card/85 overflow-hidden shadow-[0_20px_44px_-20px_hsl(var(--foreground)/0.18)]">
        {/* Tab bar — the row of tabs is wider than a phone, so it scrolls
            horizontally instead of overflowing the card (which is clipped). */}
        <div className="flex items-stretch border-b hairline overflow-x-auto">
          <div className="flex items-center gap-2 px-3 border-r hairline shrink-0">
            <span className="h-2 w-2 rounded-full bg-foreground/15" />
            <span className="h-2 w-2 rounded-full bg-foreground/15" />
            <span className="h-2 w-2 rounded-full bg-foreground/15" />
          </div>
          {WORKSPACES.map((w, i) => {
            const Icon = w.icon;
            const isActive = i === idx;
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => setIdx(i)}
                className={
                  "relative flex shrink-0 whitespace-nowrap items-center gap-1.5 px-3 py-2.5 text-[11px] font-medium tracking-tight border-r hairline transition " +
                  (isActive
                    ? "text-foreground bg-background"
                    : "text-foreground/65 hover:text-foreground/85 bg-card/60")
                }
              >
                <Icon className="h-3 w-3" />
                {w.name}
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute left-0 right-0 -bottom-px h-0.5 bg-[hsl(var(--accent))]"
                  />
                )}
              </button>
            );
          })}
          <span className="ml-auto hidden sm:block shrink-0 px-3 py-2.5 text-[9.5px] uppercase font-mono tracking-[0.18em] text-foreground/65">
            {active.domain}
          </span>
        </div>

        {/* Prompt row */}
        <div className="px-5 py-4 border-b hairline flex items-start gap-3 bg-background">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border hairline-strong shrink-0">
            <ActiveIcon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-foreground/65">
              you · just now
            </div>
            <div
              key={`p-${active.id}`}
              className="mt-1 text-[14px] leading-snug text-foreground/90 animate-[blur-fade-in_0.45s_ease-out]"
            >
              {active.prompt}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 bg-background min-h-[200px]">
          <ul
            key={`body-${active.id}`}
            className="grid grid-cols-1 sm:grid-cols-2 gap-2 animate-[blur-fade-in_0.55s_ease-out]"
          >
            {active.body.map((row) => (
              <li
                key={row.tag}
                className="rounded-md border hairline px-3 py-2 bg-card/60"
              >
                <div className="text-[9.5px] font-mono uppercase tracking-[0.16em] text-foreground/65">
                  {row.tag}
                </div>
                <div className="mt-1 text-[12px] leading-snug text-foreground/80">
                  {row.text}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t hairline bg-card/60">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.16em] text-foreground/65">
            <span
              className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse"
              aria-hidden
            />
            {active.foot}
          </span>
          <div className="flex items-center gap-1">
            {WORKSPACES.map((_, i) => (
              <span
                key={i}
                className={
                  "h-1 rounded-full transition-all " +
                  (i === idx
                    ? "w-5 bg-foreground/85"
                    : "w-1 bg-foreground/25")
                }
                aria-hidden
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
